import { useEffect, useState } from "react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { api } from "../../lib/api";
import { displayMoney } from "../../utils/pricing";

// Confirm before any paid action. confirm.estimate = {moduleId, action, sceneId} → exact
// backend preview (raw USD), shown at 1.5x; confirm.free = true shows "Free".
function useEstimate(est) {
    const [data, setData] = useState(null);
    useEffect(() => {
        setData(null);
        if (!est) return;
        const q = new URLSearchParams({ action: est.action, ...(est.sceneId ? { scene_id: est.sceneId } : {}) });
        api.get(`/modules/${est.moduleId}/cost-estimate?${q}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
    }, [est?.moduleId, est?.action, est?.sceneId]); // eslint-disable-line react-hooks/exhaustive-deps
    return data;
}

function CostBody({ confirm, data }) {
    if (confirm?.free) return <>Cost: <span className="font-medium">Free</span></>;
    if (!confirm?.estimate) return "No cost estimate is available for this action.";
    if (!data) return "Working out the cost…";
    if (data.error) return "Couldn't work out the cost right now.";
    return (
        <div className="space-y-1">
            <div>Estimated cost: <span data-testid="cost-confirm-total" className="font-medium">{data.free ? "Free" : `$${displayMoney(data.raw_usd)}`}</span></div>
            {data.parts?.map((p) => (
                <div key={p.label} className="flex justify-between text-xs text-neutral-500">
                    <span>{p.label}</span><span>{p.reused ? "$0 (reused)" : `$${displayMoney(p.raw_usd)}`}</span>
                </div>
            ))}
            {data.note && <div className="text-xs text-neutral-500">{data.note}</div>}
        </div>
    );
}

export function CostConfirm({ confirm, onClose }) {
    const open = !!confirm;
    const data = useEstimate(confirm?.estimate);
    return (
        <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
            <AlertDialogContent data-testid="cost-confirm-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
                    <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
                </AlertDialogHeader>
                <div data-testid="cost-confirm-amount" className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
                    <CostBody confirm={confirm} data={data} />
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cost-confirm-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        data-testid="cost-confirm-ok"
                        onClick={() => { const fn = confirm?.onConfirm; onClose(); fn && fn(); }}
                    >
                        {confirm?.okLabel || "Continue"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
